import { axiosInstance } from "./index.ts";

export interface User {
	email: string;
	uuid: string;
	name: string;
	password?: string;
	image?: string;
	createdAt?: string;
	modifiedAt?: string;
	roles: ROLES[];
	isVerified?: boolean;
	timeZone?: string;
	// Set by the backend on login / profile fetch when the user must (re-)accept
	// the current Terms of Service before continuing.
	tosAcceptanceRequired?: boolean;
	requiredLegalDocuments?: string[];
	legalFirstName?: string;
	legalLastName?: string;
	mobileCountryCode?: string;
	mobileNumber?: string;
	address?: UserAddress;
	agencySubrole?: "standard" | "finance";
}

export interface UserAddress {
	addressLine1?: string;
	addressLine2?: string;
	city?: string;
	state?: string;
	postalCode?: string;
	countryCode?: string;
}

export enum ROLES {
	BRAND = "brand",
	AGENCY = "agency",
	CLIENT = "client",
	USER = "user",
	ADMIN = "admin",
	CREATOR = "creator",
}

/**
 * Get user roles from sessionStorage (tab-scoped to prevent cross-account leakage).
 * @returns Array of user roles or empty array if not found
 */
export const getUserRoles = (): ROLES[] => {
	try {
		const rolesStr = sessionStorage.getItem("userRoles");
		if (rolesStr) {
			return JSON.parse(rolesStr) as ROLES[];
		}
	} catch (error) {
		console.error("Error parsing user roles from sessionStorage:", error);
	}
	return [];
};

/**
 * Check if user has a specific role
 * @param role - The role to check for
 * @returns true if user has the role, false otherwise
 */
export const hasRole = (role: ROLES): boolean => {
	const roles = getUserRoles();
	return roles.includes(role);
};

export const isAgencyFinanceUser = (user: User | null | undefined): boolean =>
	Boolean(user?.roles?.includes(ROLES.AGENCY) && user.agencySubrole === "finance");

/** Ticket-348: who may open /admin — admins, and agency users with the Finance subrole. */
export const canAccessAdmin = (user: User | null | undefined): boolean =>
	Boolean(user?.roles?.includes(ROLES.ADMIN)) || isAgencyFinanceUser(user);

export type AgencySubrole = "standard" | "finance";

export interface AdminAgencyUser {
	uuid: string;
	name: string;
	email: string;
	agencySubrole: AgencySubrole;
}

export const adminUserService = {
	listAgencyUsers: async (): Promise<AdminAgencyUser[]> => {
		const response = await axiosInstance.get("/admin/agency-users");
		return response.data;
	},
	updateAgencySubrole: async (userId: string, agencySubrole: AgencySubrole): Promise<AdminAgencyUser> => {
		const response = await axiosInstance.patch(`/admin/agency-users/${userId}/subrole`, { agencySubrole });
		return response.data;
	},
};

export const userService = {
	creatorList: () => {
		return axiosInstance.get("/user/creator/list");
	},
	findByHandleName: (handleName: string) => {
		return axiosInstance.get(`/user/creator/${handleName}`);
	},
	checkExists: async (email: string): Promise<{ email: boolean; username: boolean }> => {
		const res = await axiosInstance.get("/users/check-exists", { params: { email } });
		return res.data;
	},
	checkUsernameExists: async (username: string): Promise<{ email: boolean; username: boolean }> => {
		const res = await axiosInstance.get("/users/check-exists", { params: { username } });
		return res.data;
	},
	resendVerificationEmail: async (email: string): Promise<{ message: string }> => {
		const res = await axiosInstance.post("/users/resend-verification-email", { email });
		return res.data;
	},
	verifyEmail: async (email: string, code: string): Promise<{ message: string }> => {
		const res = await axiosInstance.post("/users/verify-email", { email, code });
		return res.data;
	},
	register: async (payload: {
		email: string;
		password: string;
		name: string;
		roles?: string[];
		profileImage?: string;
		tosAccepted?: boolean;
		tosVersion?: string;
		documentTypes?: Array<"terms_of_service" | "privacy_policy">;
		timeZone?: string;
	}) => {
		const body = { ...payload, roles: payload.roles || ["user"] };
		const res = await axiosInstance.post("/users/register", body);
		return res.data;
	},
	registerCreator: async (payload: {
		email: string;
		password: string;
		name: string;
		profileImage?: string;
		tosAccepted?: boolean;
		tosVersion?: string;
		documentTypes?: Array<"terms_of_service" | "privacy_policy">;
		timeZone?: string;
	}) => {
		const res = await axiosInstance.post("/creators/register", payload);
		return res.data;
	},
	recoverPassword: async (email: string): Promise<string> => {
		const res = await axiosInstance.post("/users/recover-password", { email });
		return res.data;
	},
	resetPassword: async (token: string, newPassword: string): Promise<string> => {
		const res = await axiosInstance.post("/users/reset-password", { token, newPassword });
		return res.data;
	},
	getCurrentUser: async () => {
		const res = await axiosInstance.get("/user/me");
		return res.data;
	},
	acceptTermsOfService: async (): Promise<{ message: string; tosVersion: string }> => {
		const res = await axiosInstance.post("/users/accept-tos");
		return res.data;
	},
};
